import { IUserIdentity, IJob } from '@models';
// @ts-ignore
import Parser from './parser';

export type Invocation = { type: 'invocation', name: string, args: any };
export type PathSegment = string | Invocation;

export interface IExpression {
    type: string;
    op?: string;
    left?: IExpression;
    right?: IExpression;
    values?: [PathSegment];
    value?: string | boolean | number | IExpression;
    // invoke
    name?: string;
    args?: any[] | any;
}

type Ctx = any;

const isSingleValue = (expr: IExpression) =>
    expr.type === 'boolean'
    || expr.type === 'number'
    || expr.type === 'string';

const specialTerms = [
    'contains',
    'departmentIds',
    'mainDepartment',
    'impliedDepartment',
    'list',
    'isWorker',
    'isContractor',
    'isGestor',
    'responsible',
    'impliedResponsible',
    'getObliqueDepartment',
    'getProductiveDepartment',
    'hasDepartmentInJob',
    'hasTeamDepartmentInJob',
    'hasResponsibleInJob',
    'hasContractorInJob',
];

const invoke = ({ name, args }: IExpression, value: any, root: any = {}) => {
    if (name?.toLowerCase() === 'contains') {
        if (typeof (value) === 'string') {
            return value?.includes(evaluate(args, root));
        }
        else if (value?.includes) {
            return value?.includes(evaluate(args, root));
        } else {
            console.error('Expressions: error invoking Contains', value, args);
            return null;
        }
    } else if (name?.toLowerCase() === "list.union" || name?.toLowerCase() === 'union') {
        const a = evaluate(args[0], root);
        const b = evaluate(args[1], root);
        return a.find((r: any) => b.includes(r)) !== undefined;
    } else if (name?.toLowerCase() === 'departmentids') {
        if (value && value.departments) {
            return value.departments.map((i: any) => typeof (i) === 'number' ? i : i.departmentId ?? i.id);
        } else {
            return false;
        }
    } else if (name?.toLowerCase() === 'maindepartment') {
        const job = value as IJob;
        if (job && job.departments) {
            return job
                .departments
                .find(d => d.isMain === true)?.departmentId ?? null;
        } else {
            console.log('Expressions: evaluating MainDepartment on no job', value);
            return false;
        }
    } else if (name?.toLowerCase() === 'implieddepartment') {
        const job = value as IJob;
        if (job && job.departments) {
            return job
                .departments
                .find(d => d.isMain === false)?.departmentId ?? null;
        } else {
            console.log('Expressions: evaluating ImpliedDepartment on no job', value);
            return false;
        }
    } else if (name?.toLowerCase() === 'responsible') {
        const job = value as IJob;
        if (job && job.departments) {
            return job
                .departments
                .find(d => d.isMain === true)?.responsibleId ?? null;
        } else {
            console.log('Expressions: evaluating responsible on no job', value);
            return false;
        }
    } else if (name?.toLowerCase() === 'impliedresponsible') {
        const job = value as IJob;
        if (job && job.departments) {
            return job
                .departments
                .find(d => d.isMain === false)?.responsibleId ?? null;
        } else {
            console.log('Expressions: evaluating implied responsible on no job', value);
            return false;
        }
    } else if (name?.toLowerCase() === 'hasdepartment') {
        let identity = value as IUserIdentity;

        if (value === undefined) {
            identity = root.identity;
        }
        if (identity && identity.departments) {
            const needle = evaluate(args, root);
            const res = identity.departments.find((d: any) => {
                if (typeof d === 'number') {
                    return d === needle;
                }
                else if ('departmentId' in d) {
                    return d.departmentId === needle;
                } else {
                    return d.id === needle;
                }
            });
            return res !== undefined;
        } else {
            console.log('Expressions: evaluating HasDepartment on identity with no departments', value, args);
            return false;
        }
    } else if (name?.toLowerCase() === 'anydepartment') {
        let identity = value as IUserIdentity;

        if (value === undefined) {
            identity = root.identity;
        }
        if (identity && identity.departments) {
            const needles = evaluate(args, root);
            const res = identity
                .departments
                .find(d => needles.indexOf(d) >= 0) !== undefined;
            return res;
        } else {
            console.log('Expressions: evaluating AnyDepartment on identity with no departments', value);
            return false;
        }
    } else if (name?.toLowerCase() === 'getobliquedepartment') {
        if (value && value.departments && root && root.work && root.work.departments) {
            const userDepartmentIds = value.departments;
            const workDepartments = root.work.departments;
            const userDepartments = workDepartments.filter((department: any) => userDepartmentIds.includes(department.id));
            return userDepartments.find((d: any) => d.isOblique === true)?.id ?? null;
        } else {
            console.log('Expressions: evaluating GetObliqueDepartment on no department', value);
            return false;
        }
    } else if (name?.toLowerCase() === 'getproductivedepartment') {
        if (value && value.departments && root && root.work && root.work.departments) {
            const userDepartmentIds = value.departments;
            const workDepartments = root.work.departments;
            const userDepartments = workDepartments.filter((department: any) => userDepartmentIds.includes(department.id));
            return userDepartments.find((d: any) => d.isOblique === false)?.id ?? null;
        } else {
            console.log('Expressions: evaluating GetProductiveDepartment on no department', value);
            return false;
        }
    }
    else if (name?.toLowerCase() === 'haspolicy') {
        let identity = value as IUserIdentity;

        if (value === undefined) {
            identity = root.identity;
        }

        if (identity && identity.policies) {
            const res = identity.policies.indexOf(evaluate(args)) >= 0;
            return res;
        } else {
            console.log('Expressions: evaluating HasPolicy on non identity', value);
            return false;
        }
    } else if (name?.toLowerCase() === 'hasworker') {
        let identity = value as IUserIdentity;

        if (value === undefined) {
            identity = root.identity;
        }

        if (identity && identity.workerIds) {
            const needle = evaluate(args, root);
            const res = identity.workerIds.find(d => d === needle) !== undefined;
            return res;
        } else {
            console.log('Expressions: evaluating HasWorker on non identity', value);
            return false;
        }
    } else if (name?.toLowerCase() === 'isworker') {
        let identity = value as IUserIdentity;

        if (value === undefined) {
            identity = root.identity;
        }

        if (identity && identity.roles) {
            const res = identity.roles.indexOf("worker") >= 0;
            return res;
        }
        else {
            console.log('Expressions: evaluating IsWorker on non identity', value);
            return false;
        }
    } else if (name?.toLowerCase() === 'iscontractor') {
        let identity = value as IUserIdentity;

        if (value === undefined) {
            identity = root.identity;
        }

        if (identity?.roles) {
            const res = identity.roles.indexOf("contractor") >= 0;
            return res;
        }
        else {
            console.log('Expressions: evaluating IsContractor on non identity', value);
            return false;
        }
    } else if (name?.toLowerCase() === 'isgestor') {
        let identity = value as IUserIdentity;

        if (value === undefined) {
            identity = root.identity;
        }

        if (identity?.roles) {
            const res = identity.roles.indexOf("gestor") >= 0;
            return res;
        } else {
            console.log('Expressions: evaluating IsGestor on non identity', value);
            return false;
        }
    } else if (name?.toLowerCase() === 'hasdepartmentinjob') {
        let identity = value as IUserIdentity;


        if (value === undefined) {
            identity = root.identity;
        }

        const job = evaluate(args, root) as IJob;

        if (identity && identity.departments && job && job.departments) {
            const jobDeptIds = job.departments
                .filter((d: any) => d.isActive !== false)
                .map((d: any) => d.departmentId ?? d.id);
            const userDeptIds = identity.departments.map((d: any) =>
                typeof d === 'number' ? d : d.departmentId ?? d.id);
            return jobDeptIds.some((did: number) => userDeptIds.includes(did));
        } else {
            if (!job) return false;
            console.log('Expressions: evaluating HasDepartmentInJob missing data', value, job);
            return false;
        }
    } else if (name?.toLowerCase() === 'hasteamdepartmentinjob') {
        let identity = value as IUserIdentity;

        if (value === undefined) {
            identity = root.identity;
        }

        const job = evaluate(args, root) as IJob;

        if (identity && identity.departments && job && job.departments) {
            const jobDeptIds = job.departments
                .filter((d: any) => d.isActive !== false)
                .map((d: any) => d.departmentId ?? d.id);
            const teamDeptIds = identity.departments
                .filter((d: any) =>
                    typeof d === 'number'
                        ? !identity.roleMarkerDepartments?.includes(d)
                        : !identity.roleMarkerDepartments?.includes(d.departmentId ?? d.id))
                .map((d: any) => typeof d === 'number' ? d : d.departmentId ?? d.id);
            return jobDeptIds.some((did: number) => teamDeptIds.includes(did));
        } else {
            if (!job) return false;
            console.log('Expressions: evaluating HasTeamDepartmentInJob missing data', value, job);
            return false;
        }
		} else if (name?.toLowerCase() === 'isresponsibleinjob') {
      let identity = value as IUserIdentity;

      if (value === undefined) {
        identity = root.identity;
      }

      const job = evaluate(args, root) as IJob;

      if (identity?.workerIds && job?.departments) {
        const responsibleIds = job.departments
          .filter((d: any) => d.isActive !== false)
          .map((d: any) => d.responsibleId);
        return responsibleIds.some((rid: number) => identity.workerIds?.includes(rid));
      } else {
        if (!job) return false;
        console.log('Expressions: evaluating IsResponsibleInJob missing data', value, job);
        return false;
      }
    } else if (name?.toLowerCase() === 'hascontractorinjob') {
      let identity = value as IUserIdentity;

      if (value === undefined) {
        identity = root.identity;
      }

      const job = evaluate(args, root) as IJob;

      if (identity?.contractorIds && job?.contractors) {
        return job.contractors.some((c: any) =>
          identity.contractorIds?.includes(c.contractorId));
      } else {
        if (!job) return false;
        console.log('Expressions: evaluating HasContractorInJob missing data', value, job);
        return false;
      }
    } else if (name?.toLowerCase() === 'hascontractorinchain') {
      let identity = value as IUserIdentity;
      if (value === undefined) {
        identity = root.identity;
      }

      if (!identity?.contractorIds || identity.contractorIds.length === 0) return false;

      const job = evaluate(args[0], root) as IJob;
      const contractorId = evaluate(args[1], root);

      if (!job?.contractors || contractorId == null) return false;

      const jhcList = job.contractors.filter((c: any) => c.contractorId === contractorId);
      if (jhcList.length === 0) return false;

      const parentMap = new Map(job.contractors.map((c: any) => [c.id, c]));

      for (const jhc of jhcList) {
        if (identity.contractorIds.includes(jhc.contractorId)) return true;

        const visited = new Set<number>();
        let parentId = jhc.parentId;
        while (parentId != null && !visited.has(parentId)) {
          visited.add(parentId);
          const parent = parentMap.get(parentId);
          if (!parent) break;
          if (identity.contractorIds.includes(parent.contractorId)) return true;
          parentId = parent.parentId;
        }
      }
      return false;
    }
}

const evaluatePathSegment = (segment: PathSegment, values: any[], root: any = {}) => {
    const currentCtx = values[values.length - 1];

  if (currentCtx === null || currentCtx === undefined) {
        return undefined;
  }

    if (typeof (segment) === 'string' && !specialTerms.includes(segment)) {
        return currentCtx[segment];
    }
    else if (specialTerms.includes(segment as string)) {
        return invoke({ name: segment as string, type: 'string' }, currentCtx, root);
    }
    else { // invocation
        return invoke(segment as IExpression, currentCtx, root);
    }
}

const toIdentifier = (s: string) => {
    return s.substring(0, 1).toLowerCase() + s.substring(1);
}

const evaluatePath = (values: PathSegment[], ctx: Ctx) => {
    const stack: any[] = [ctx];
    const segments: PathSegment[] = values.map(s =>
        typeof (s) === 'string' ? toIdentifier(s as string) : s);

    for (const v of segments) {
        stack.push(evaluatePathSegment(v, stack, ctx));
    }

    return stack[stack.length - 1];
}

const evaluateBinaryOperator: (expr: IExpression, ctx: Ctx) => any = (expr: IExpression, ctx: Ctx) => {
    if (expr.op === 'AND') {
        const leftRes = evaluate(expr.left!, ctx);
        const rightRes = evaluate(expr.right!, ctx);
        return leftRes && rightRes;
    }
    else if (expr.op === 'OR') {
        return (evaluate(expr.left!, ctx) === true) || (evaluate(expr.right!, ctx) === true);
    }
    else if (expr.op === '==') {
        return evaluate(expr.left!, ctx) === evaluate(expr.right!, ctx);
    }
    else if (expr.op === '!=') {
        return evaluate(expr.left!, ctx) !== evaluate(expr.right!, ctx);
    }
    else if (expr.op === '<') {
        return evaluate(expr.left!, ctx) < evaluate(expr.right!, ctx);
    }
    else if (expr.op === '>') {
        return evaluate(expr.left!, ctx) > evaluate(expr.right!, ctx);
    }
    else if (expr.op === '<=') {
        return evaluate(expr.left!, ctx) <= evaluate(expr.right!, ctx);
    }
    else if (expr.op === '>=') {
        return evaluate(expr.left!, ctx) >= evaluate(expr.right!, ctx);
    }
    else if (expr.op === '+') {
        return evaluate(expr.left!, ctx) + evaluate(expr.right!, ctx);
    }
    else if (expr.op === '-') {
        return evaluate(expr.left!, ctx) - evaluate(expr.right!, ctx);
    }
    else if (expr.op === '*') {
        return evaluate(expr.left!, ctx) * evaluate(expr.right!, ctx);
    }
    else if (expr.op === '/') {
        return evaluate(expr.left!, ctx) / evaluate(expr.right!, ctx);
    }
}

export function evaluate<T>(expr: IExpression, ctx: Ctx = undefined) {
    if (isSingleValue(expr)) {
        return expr.value!;
    }
    else if (expr.type === 'binaryop') {
        return evaluateBinaryOperator(expr, ctx);
    }
    else if (expr.type === 'path') {
        return evaluatePath(expr.values!, ctx);
    }
}
//  LLAMAR A ESTO
export function evalAndParse(expression: string, ctx: Ctx = undefined) {
    return evaluate(parse(expression), ctx);
}

export function parse(expression: string) {
    return Parser.parse(expression.trim()) as IExpression;
}
